<template>
  <ColumnEdit
    v-if="!pending"
    class="page-content px-3 pt-2"
    :edit="!!selected"
    :modal="detailsInModal"
  >
    <div class="d-flex mb-3">
      <div
        class="page-route"
        :class="{ active: tab === 'planned' }"
        @click="tab = 'planned'"
      >
        {{ $t('notify.plannedTab') }}
      </div>
      <div
        class="page-route"
        :class="{ active: tab === 'past' }"
        @click="tab = 'past'"
      >
        {{ $t('notify.pastTab') }}
      </div>
    </div>

    <template v-if="tab === 'planned'">
      <div
        v-for="s in waitingSchedule"
        :key="s.scheduleId"
      >
        <NotificationScheduleRow
          v-bind="s"
          class="mt-1"
          :selected="isSelected(s)"
          @click.native="toggle(s)"
        />
      </div>
      <div
        v-if="waitingSchedule.length === 0"
        class="text-muted"
      >
        {{ $t('notify.noPlannedNotifications') }}
      </div>
    </template>

    <template v-if="tab === 'past'">
      <div
        class="mb-3"
        style="max-width: 200px"
      >
        <MonthDropdown
          :selected.sync="selectedMonth"
          :title="$t('time.month')"
          small
          allow-none
        />
      </div>

      <div
        v-if="pastPending"
        class="text-center py-3"
      >
        <Loader />
      </div>
      <template v-else>
        <div
          v-for="s in pastSchedule"
          :key="s.scheduleId"
        >
          <NotificationScheduleRow
            v-bind="s"
            class="mt-1"
            :selected="isSelected(s)"
            @click.native="toggle(s)"
          />
        </div>
        <div
          v-if="pastSchedule.length === 0"
          class="text-muted text-center"
        >
          {{ $t('notify.noPastNotifications') }}
        </div>
      </template>
    </template>

    <template #edit>
      <Transition
        name="client-details"
        mode="out-in"
      >
        <div
          v-if="selected && detailsReady"
          class="page-cart position-relative ml-2 h-100"
          style="overflow-y: scroll"
        >
          <CloseButton
            style="position: absolute; right: 3px; top: 3px; z-index: 100"
            @click.native.stop="selected = false; detailsReady = false"
          />

          <NotificationDetails
            v-bind="selected"
            @close="selected = null"
            @refresh="selected = null; request()"
          />
        </div>
      </Transition>
    </template>
  </ColumnEdit>
  <div v-else>
    <Loader />
  </div>
</template>

<script>
import ColumnEdit from '@/components/layout/ColumnEdit';
import NotificationDetails from '@/components/notifications/NotificationDetails';
import NotificationScheduleRow from '@/components/notifications/NotificationScheduleRow';
import MonthDropdown from '@/components/shop/MonthDropdown';
import moment from 'moment';
import { mapActions } from 'vuex';

export default {
  data: () => ({
    tab: 'planned',
    notificationSchedule: [],
    pastNotificationSchedule: [],
    pending: false,
    pastPending: false,
    selected: null,
    detailsReady: false,
    detailsInModal: false,
    selectedMonth: null,
  }),
  components: {
    ColumnEdit,
    NotificationDetails,
    NotificationScheduleRow,
    MonthDropdown,
  },
  computed: {
    waitingSchedule() {
      return this.notificationSchedule
        .filter((x) => !x.delivered && !x.skipped)
        .sort((a, b) => moment(a.timestamp).unix() - moment(b.timestamp).unix());
    },
    pastSchedule() {
      return this.pastNotificationSchedule
        .filter((x) => x.delivered || x.skipped)
        .sort((a, b) => moment(b.delivered || b.skipped).unix() - moment(a.delivered || a.skipped).unix());
    },
  },
  watch: {
    selectedMonth() {
      this.requestPast();
    },
    tab(val) {
      if (val === 'past' && this.pastNotificationSchedule.length === 0 && !this.pastPending) {
        this.requestPast();
      }
    },
  },
  methods: {
    ...mapActions([
      'getNotificationsPlan',
    ]),
    isSelected({ scheduleId }) {
      return this.selected && this.selected.scheduleId === scheduleId;
    },
    toggle(s) {
      if (this.isSelected(s)) {
        this.selected = null;
        this.detailsReady = false;
      } else {
        this.selected = s;
        this.timeout = setTimeout(() => {
          this.detailsReady = true;
        }, 200);
      }
    },
    request() {
      this.pending = true;
      this.getNotificationsPlan({
        params: {
          query: {
            from: moment().utc().startOf('day').format(),
            to: moment().utc().add(60, 'day').format(),
          },
        },
      })
        .then(({ data }) => {
          this.notificationSchedule = data;
        })
        .finally(() => {
          this.pending = false;
        });
    },
    requestPast() {
      this.pastPending = true;
      const from = this.selectedMonth
        ? moment(this.selectedMonth.from).utc().startOf('month').format()
        : moment().utc().subtract(60, 'days').startOf('day')
          .format();

      const to = this.selectedMonth
        ? moment(this.selectedMonth.to).utc().endOf('month').format()
        : moment().utc().add(7, 'days').format();

      this.getNotificationsPlan({
        params: {
          query: { from, to },
        },
      })
        .then(({ data }) => {
          this.pastNotificationSchedule = data;
        })
        .finally(() => {
          this.pastPending = false;
        });
    },
  },
  created() {
    this.$emit('page', 'notifications');
    this.request();
  },
};
</script>

<style lang="scss" scoped>
  .client-details-enter-active,
  .client-details-leave-active {
    transition: opacity 200ms, transform 200ms;
  }

  .client-details-enter, .client-details-leave-to {
    opacity: 0;
    transform: translateX(-10px);
  }

  .page-content {
    height: calc(100vh - #{$navbarHeight} - 240px);
    overflow-x: hidden;
  }
</style>
